import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { firstValueFrom, map } from 'rxjs'
import { ForgotPasswordService, NotificationChannel } from 'superlikers-microsites-api'

import { TogglePasswordDirective } from '@shared/directives'
import { passwordShouldMatch } from '@shared/validators'

@Component({
  selector: 'reset-password-page',
  imports: [ReactiveFormsModule, TogglePasswordDirective],
  standalone: true,
  templateUrl: './reset-password-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-4'
  }
})
export class ResetPasswordPageComponent {
  fb = inject(FormBuilder)
  route = inject(ActivatedRoute)
  router = inject(Router)
  forgotPasswordService = inject(ForgotPasswordService)

  loading = signal<boolean>(false)
  error = signal<string>('')

  token = toSignal(this.route.queryParamMap.pipe(map(params => params.get('token'))))
  channel = toSignal(this.route.queryParamMap.pipe(map(params => params.get('channel'))))

  constructor() {
    effect(() => {
      if (!this.token() || !this.channel()) {
        this.router.navigate(['/'])
      }
    })
  }

  form = this.fb.group(
    {
      password: ['', [Validators.required, Validators.minLength(6)]],
      password_confirmation: ['', [Validators.required, Validators.minLength(6)]]
    },
    {
      validators: passwordShouldMatch
    }
  )

  async submit() {
    if (this.form.invalid) return

    this.loading.set(true)
    this.error.set('')

    const { password, password_confirmation } = this.form.value
    if (!password || !password_confirmation) return

    const channel = this.channel() as NotificationChannel
    const token = this.token()
    if (!channel || !token) return

    const response$ = this.forgotPasswordService.updatePassword(password, password_confirmation, token, channel)
    const response = await firstValueFrom(response$)

    if (response.state === 'success') {
      this.router.navigate(['/'])
    } else {
      this.error.set(response.message)
    }

    this.loading.set(false)
  }
}
